import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import CheckPermission from './CheckPermission';
import { useUser } from './UserContext';
import { hasAnyPermission } from '../interfaces/Permissions';
import { CLIENT_ACCOUNT_ACCESS_PERMISSIONS } from '../interfaces/Account';
import { SETTINGS_NAV_GROUPS } from './SettingsNavBar';
import { LuActivity, LuTable, LuFileText, LuSignature, LuPercent, LuClock, LuSettings, LuTags, LuBan } from 'react-icons/lu';
import { TbUsers, TbCurrencyDollar, TbChartHistogram } from 'react-icons/tb';
import { IoMenu, IoClose, IoDocumentOutline } from 'react-icons/io5';
import { IoMdAddCircleOutline } from 'react-icons/io';
import {
  FaChevronDown,
  FaChevronUp,
  FaRegChartBar,
  FaRegCalendarAlt,
  FaFileInvoiceDollar,
  FaUsers,
  FaTags,
  FaRegClipboard,
  FaMicrophone,
} from 'react-icons/fa';
import { HiTemplate, HiDocumentReport, HiChartBar } from 'react-icons/hi';
import { HiOutlineUser } from 'react-icons/hi2';
import { PiShippingContainerBold } from 'react-icons/pi';
import { RiBillLine, RiMoneyDollarCircleLine } from 'react-icons/ri';
import { LiaShipSolid } from 'react-icons/lia';
import { AiOutlineProfile, AiOutlineFileExcel } from 'react-icons/ai';
import {
  MdRoute,
  MdManageAccounts,
  MdOutlineEmail,
  MdDashboard,
  MdOutlineImportExport,
  MdOutlineSwapHoriz,
  MdDirectionsBoat,
} from 'react-icons/md';
import logo from '../assets/logo.png';
import logoDark from '../assets/logo-dark.png';
import { useTheme } from '../contexts/ThemeContext';

type MenuIcon = React.ComponentType<{ className?: string }>;

interface MenuItemProps {
  id: string;
  title: string;
  to?: string;
  icon?: MenuIcon;
  children?: React.ReactNode;
}

export default function MobileMenu() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());
  const { theme } = useTheme();
  const { user } = useUser();

  const toggleSubmenu = (id: string) => {
    setOpenSubmenus((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const MenuItem = ({ title, to = '', icon: Icon, children = null, id }: MenuItemProps) => {
    const hasSubmenu = Boolean(children);
    const isSubmenuOpen = openSubmenus.has(id);

    if (hasSubmenu) {
      return (
        <div className="border-b border-gray-200 dark:border-dm-border">
          <div
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-dm-hover"
            onClick={() => toggleSubmenu(id)}
          >
            <div className="flex items-center gap-3">
              {Icon && <Icon className="w-5 h-5" />}
              <span className="font-medium">{title}</span>
            </div>
            {isSubmenuOpen ? (
              <FaChevronUp className="w-5 h-5 text-gray-500 dark:text-dm-text-muted" />
            ) : (
              <FaChevronDown className="w-5 h-5 text-gray-500 dark:text-dm-text-muted" />
            )}
          </div>

          {isSubmenuOpen && <div className="bg-gray-50 dark:bg-dm-surface pl-8">{children}</div>}
        </div>
      );
    }

    return (
      <NavLink
        to={to}
        className={({ isActive }) => `
          flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-dm-hover border-b border-gray-200 dark:border-dm-border
          ${isActive ? 'text-darkprimary dark:text-primary font-medium' : 'text-gray-800 dark:text-dm-text'}
        `}
        onClick={() => setIsMenuOpen(false)}
      >
        {Icon && <Icon className="w-5 h-5" />}
        {title}
      </NavLink>
    );
  };

  const visibleSettingsGroups = SETTINGS_NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => hasAnyPermission(user, item.permissions)),
  })).filter((group) => group.items.length > 0);

  const menuItems = () => (
    <>
      <CheckPermission permissions={['CLIENT_VIEW_ALL', 'CLIENT_VIEW', 'ACTIVITY_VIEW_ALL', 'ACTIVITY_VIEW']}>
        <MenuItem id="crm" title="CRM">
          <MenuItem id="crmDashboard" title="Dashboard" to="/crm/dashboard" icon={MdDashboard} />
          <CheckPermission permissions={['CLIENT_VIEW_ALL', 'CLIENT_VIEW']}>
            <MenuItem id="clienti" title="Clienți" to="/crm/clients/index" icon={TbUsers} />
          </CheckPermission>
          <CheckPermission permissions={['ACTIVITY_VIEW_ALL', 'ACTIVITY_VIEW']}>
            <MenuItem id="activitate" title="Activitate" to="/crm/clients/activities" icon={LuActivity} />
          </CheckPermission>
          <CheckPermission permissions={['BOOKINGS_VIEW_ALL']}>
            <MenuItem id="containereFob" title="Containere FOB" to="/crm/clients/bookings" icon={FaRegChartBar} />
          </CheckPermission>
          <CheckPermission permissions={['OFFER_VIEW_ALL', 'OFFER_VIEW']}>
            <MenuItem id="bookinguriDeOfertat" title="Booking-uri de ofertat" to="/crm/clients/booking-offers" icon={FaTags} />
          </CheckPermission>
          <CheckPermission permissions={['EXPORT_INSTRUCTIONS_MANAGE', 'EXPORT_INSTRUCTIONS_MANAGE_ALL']}>
            <MenuItem id="instructiuniExport" title="Instrucțiuni export" to="/crm/clients/export-instructions" icon={FaRegChartBar} />
          </CheckPermission>
          <MenuItem id="calendar" title="Calendar" to="/crm/calendar" icon={FaRegCalendarAlt} />
          <CheckPermission permissions={['CLIENT_VIEW_ALL', 'CLIENT_VIEW']}>
            <MenuItem id="grupuriClienti" title="Grupuri Clienți" to="/crm/clients/groups" icon={FaUsers} />
          </CheckPermission>
          <CheckPermission permissions={['CLIENT_VIEW_ALL', 'CLIENT_VIEW']}>
            <MenuItem id="rapoarteVizite" title="Vizite" to="/crm/visit-reports/index" icon={FaRegClipboard} />
          </CheckPermission>
          <MenuItem id="sedinte" title="Ședințe" to="/crm/sedinte/index" icon={FaMicrophone} />
        </MenuItem>
      </CheckPermission>

      <CheckPermission permissions={['OFFER_VIEW_ALL', 'OFFER_VIEW']}>
        <MenuItem id="offers" title="Oferte">
          <CheckPermission permissions={['OFFER_VIEW', 'OFFER_VIEW_ALL']}>
            <MenuItem id="oferte" title="Oferte" to="/crm/offers/index" icon={LuTable} />
          </CheckPermission>
          <CheckPermission permissions={['OFFER_CREATE']}>
            <MenuItem id="adaugaOferta" title="Adaugă ofertă" to="/crm/offers/form" icon={IoMdAddCircleOutline} />
          </CheckPermission>
          <CheckPermission permissions={['OFFER_VIEW', 'OFFER_VIEW_ALL']}>
            <MenuItem id="sabloane" title="Șabloane" to="/crm/offers/templates" icon={HiTemplate} />
          </CheckPermission>
          <CheckPermission permissions={['OFFER_CREATE']}>
            <MenuItem id="ofertaMultipla" title="Ofertă multiplă" to="/crm/offers/multiform" icon={IoMdAddCircleOutline} />
          </CheckPermission>
          <CheckPermission permissions={['RATE_VIEW']}>
            <MenuItem id="rate" title="Rate" to="/rates/index" icon={TbCurrencyDollar} />
          </CheckPermission>
          <CheckPermission permissions={['OFFER_VIEW', 'OFFER_VIEW_ALL', 'SETTING_VIEW_ALL']}>
            <MenuItem id="pointToPoint" title="Point to Point" to="/crm/offers/point-to-point" icon={MdRoute} />
          </CheckPermission>
          <CheckPermission permissions={['NEWSLETTER_VIEW_ALL', 'NEWSLETTER_SEND_ALL']}>
            <MenuItem id="newsletter" title="Newsletter" to="/crm/newsletter/index" icon={MdOutlineEmail} />
          </CheckPermission>
        </MenuItem>
      </CheckPermission>

      <CheckPermission permissions={['BOOKINGS_VIEW_ALL']}>
        <MenuItem id="bookings" title="Bookinguri">
          <CheckPermission permissions={['BOOKINGS_VIEW_ALL']}>
            <MenuItem id="bookinguriImport" title="Bookinguri Import" to="/bookings/index" icon={LuTable} />
          </CheckPermission>
          <CheckPermission permissions={['BOOKINGS_EXPORT_VIEW_ALL']}>
            <MenuItem id="bookinguriExport" title="Bookinguri Export" to="/bookings/export" icon={LuTable} />
          </CheckPermission>
          <CheckPermission permissions={['MARKET_VIEW_ALL']}>
            <MenuItem id="piataMaritima" title="Piața maritimă" to="/bookings/piata/index" icon={TbChartHistogram} />
          </CheckPermission>
          <CheckPermission permissions={['MOVEMENTS_VIEW_ALL']}>
            <MenuItem id="movements" title="Mișcări" to="/bookings/movements" icon={PiShippingContainerBold} />
          </CheckPermission>
          <CheckPermission permissions={['BOOKINGS_VIEW_ALL']}>
            <MenuItem id="bills" title="B/L's" to="/bookings/bills/index" icon={RiBillLine} />
          </CheckPermission>
          <CheckPermission permissions={['BOOKINGS_VIEW_ALL']}>
            <MenuItem id="emailuriAvizari" title="Emailuri Avizari" to="/bookings/emails/index" icon={RiBillLine} />
          </CheckPermission>
          <CheckPermission permissions={['BOOKINGS_VIEW_ALL']}>
            <MenuItem id="statistici" title="Statistici" to="/bookings/reports" icon={FaRegChartBar} />
          </CheckPermission>
        </MenuItem>
      </CheckPermission>

      <CheckPermission permissions={['MANIFESTS_VIEW_ALL']}>
        <MenuItem id="manifeste" title="Manifeste">
          <MenuItem id="manifesteCargo" title="Cargo" to="/bookings/manifests" icon={IoDocumentOutline} />
          <MenuItem id="manifesteFreight" title="Freight" to="/bookings/freights" icon={RiMoneyDollarCircleLine} />
        </MenuItem>
      </CheckPermission>

      <CheckPermission permissions={['BOOKINGS_VIEW_ALL']}>
        <MenuItem id="containere" title="Containere">
          <MenuItem id="containereExport" title="Containere export" to="/containers/index" icon={PiShippingContainerBold} />
          <MenuItem id="containereImport" title="Containere import" to="/containers/import" icon={MdOutlineImportExport} />
          <MenuItem id="configurariContainere" title="Configurari containere" to="/settings/containers/index" icon={AiOutlineProfile} />
        </MenuItem>
      </CheckPermission>

      <CheckPermission permissions={['BOOKINGS_VIEW_ALL']}>
        <MenuItem id="terminal" title="Terminal">
          <MenuItem id="stocTerminal" title="Stoc terminal" to="/terminal/stoc" icon={PiShippingContainerBold} />
          <MenuItem id="intrariIesiri" title="Intrări & ieșiri" to="/terminal/miscari" icon={MdOutlineSwapHoriz} />
          <CheckPermission permissions={['SETTING_VIEW_ALL']}>
            <MenuItem id="fisiereTerminal" title="Fișiere" to="/terminal/fisiere" icon={AiOutlineFileExcel} />
          </CheckPermission>
        </MenuItem>
      </CheckPermission>

      <CheckPermission permissions={['INVOICE_VIEW_ALL', 'INVOICE_VIEW']}>
        <MenuItem id="documente" title="Documente">
          <CheckPermission permissions={['INVOICE_VIEW_ALL', 'INVOICE_VIEW']}>
            <MenuItem id="documenteBookinguri" title="Bookinguri" to="/facturi/bookings" icon={LiaShipSolid} />
            <MenuItem id="documenteFacturi" title="Facturi" to="/facturi/index" icon={LuFileText} />
            <MenuItem id="documenteDemurrage" title="Contrastalii & Depozitare" to="/facturi/demurrage" icon={LuClock} />
          </CheckPermission>
          <CheckPermission permissions={['CONTRACT_VIEW_ALL', 'CONTRACT_VIEW']}>
            <MenuItem id="documenteContracte" title="Contracte" to="/facturi/contracte" icon={LuSignature} />
          </CheckPermission>
          <CheckPermission permissions={['BOOKINGS_VIEW_ALL']}>
            <MenuItem id="facturiNeplatite" title="Facturi neplătite" to="/facturi/unpaid-invoices" icon={FaFileInvoiceDollar} />
          </CheckPermission>
          <CheckPermission permissions={['EXCHANGE_RATE_VIEW', 'EXCHANGE_RATE_MANAGE', 'INVOICE_VIEW_ALL', 'INVOICE_VIEW']}>
            <MenuItem id="cursuriValutare" title="Cursuri valutare" to="/facturi/cursuri" icon={TbCurrencyDollar} />
          </CheckPermission>
          <CheckPermission permissions={['VAT_RATE_VIEW', 'VAT_RATE_MANAGE', 'INVOICE_VIEW_ALL', 'INVOICE_VIEW']}>
            <MenuItem id="coteTva" title="Cote TVA" to="/facturi/cote-tva" icon={LuPercent} />
          </CheckPermission>
          <CheckPermission permissions={['INVOICE_SERIES_MANAGE', 'INVOICE_SETTINGS_MANAGE', 'INVOICE_VIEW_ALL']}>
            <MenuItem id="documenteSetari" title="Setări" to="/facturi/setari" icon={LuSettings} />
          </CheckPermission>
        </MenuItem>
      </CheckPermission>

      <CheckPermission permissions={['PRICE_VIEW_ALL', 'PRICE_MANAGE_ALL']}>
        <MenuItem id="preturi" title="Prețuri">
          <MenuItem id="preturiToate" title="Toate prețurile" to="/preturi/toate" icon={LuTags} />
          <MenuItem id="preturiLocale" title="Locale" to="/preturi/locale" icon={FaTags} />
          <MenuItem id="preturiContrastalii" title="Contrastalii" to="/preturi/contrastalii" icon={LuClock} />
          <MenuItem id="preturiRestrictii" title="Restricții" to="/preturi/restrictii" icon={LuBan} />
        </MenuItem>
      </CheckPermission>

      <CheckPermission permissions={['USER_VIEW_ALL', 'ROLE_VIEW_ALL', ...CLIENT_ACCOUNT_ACCESS_PERMISSIONS]}>
        <MenuItem id="utilizatori" title="Utilizatori">
          <CheckPermission permissions={['USER_VIEW_ALL']}>
            <MenuItem id="users" title="Utilizatori" to="/hr/users/index" icon={TbUsers} />
          </CheckPermission>
          <CheckPermission permissions={['ROLE_VIEW_ALL']}>
            <MenuItem id="roluri" title="Roluri" to="/hr/roles/index" icon={LuActivity} />
          </CheckPermission>
          <CheckPermission permissions={CLIENT_ACCOUNT_ACCESS_PERMISSIONS}>
            <MenuItem id="conturi" title="Conturi clienti" to="/settings/accounts" icon={MdManageAccounts} />
          </CheckPermission>
        </MenuItem>
      </CheckPermission>

      <CheckPermission permissions={['SETTING_VIEW_ALL']}>
        <MenuItem id="setari" title="Setări">
          {visibleSettingsGroups.map((group) => {
            const GroupIcon = group.icon;
            return (
              <MenuItem key={group.label} id={`setari-${group.label}`} title={group.label} icon={GroupIcon}>
                {group.items.map((item) => {
                  const ItemIcon = item.icon;
                  return <MenuItem key={item.to} id={item.to} title={item.label} to={item.to} icon={ItemIcon} />;
                })}
              </MenuItem>
            );
          })}
        </MenuItem>
      </CheckPermission>

      <CheckPermission permissions={['DASHBOARD_VIEW_ALL']}>
        <MenuItem id="dashboard" title="Dashboard">
          <CheckPermission permissions={['DASHBOARD_VIEW_ALL']}>
            <MenuItem id="dashboardIndex" title="Dashboard" to="/dashboard" icon={PiShippingContainerBold} />
          </CheckPermission>
          <MenuItem id="salesDashboard" title="Sales Dashboard" to="/sales-dashboard" icon={HiOutlineUser} />
        </MenuItem>
      </CheckPermission>

      <CheckPermission permissions={['DASHBOARD_VIEW_ALL']}>
        <MenuItem id="rapoarte" title="Rapoarte">
          <MenuItem id="rapoarteIndex" title="Rapoarte" to="/rapoarte/index" icon={HiDocumentReport} />
          <MenuItem id="raportActivitati" title="Raport activități" to="/rapoarte/index/activitati" icon={HiChartBar} />
        </MenuItem>
      </CheckPermission>

      <CheckPermission permissions={['SETTING_VIEW_ALL']}>
        <MenuItem id="vesselTracking" title="Vessel Tracking" to="/vessel-tracking" icon={MdDirectionsBoat} />
      </CheckPermission>

      <MenuItem id="setariProfil" title="Setări profil" to="/settings/profile" icon={AiOutlineProfile} />
    </>
  );

  return (
    <>
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="md:hidden fixed top-2 right-4 z-50 p-2 rounded-lg bg-white dark:bg-dm-surface shadow-md hover:bg-gray-50 dark:hover:bg-dm-hover"
      >
        {isMenuOpen ? <IoClose className="w-6 h-6 dark:text-dm-text" /> : <IoMenu className="w-6 h-6 dark:text-dm-text" />}
      </button>

      <div
        className={`
          fixed top-0 left-0 w-full h-full bg-white dark:bg-dm-bg dark:text-dm-text shadow-xl transition-transform duration-300 ease-in-out transform md:hidden
          ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}
          z-40
        `}
      >
        <img src={theme === 'dark' ? logoDark : logo} alt="Logo" className="absolute top-1 left-3 max-h-[50px]" />
        <div className="pt-16 pb-4 overflow-y-auto h-full">{menuItems()}</div>
      </div>

      <div
        className={`
          fixed inset-0 bg-gray-800 bg-opacity-50 transition-opacity duration-300 md:hidden
          ${isMenuOpen ? 'opacity-100 z-30' : 'opacity-0 pointer-events-none'}
        `}
        onClick={() => setIsMenuOpen(false)}
      />
    </>
  );
}
